import { Controller } from '@hotwired/stimulus';
import { showToast } from '../lib/toastr';

// Drives the embedded Lemonway card form (Hosted Fields).
//
// The SDK mounts one Lemonway-hosted iframe per field, so card data never
// touches our DOM. On submit it hands back a redirection descriptor that we
// replay into a nested iframe: this keeps the 3-D Secure step inside the
// slide-over instead of navigating the whole page away. The result comes back
// as a postMessage sent by iframe_callback_controller.
//
// Field appearance relies on the SDK's CSS class toggling rather than on the
// field event hooks: the container gets is-invalid, so Bootstrap displays the
// sibling .invalid-feedback on its own. Only the two form-level hooks need JS.
//
// Actions (declared in the template):
//   message@window->card-payment#handleMessage
//   click->card-payment#submit (on the submit target)

// The SDK cannot tell us that a field silently failed to mount, and a 3-D
// Secure page that refuses to load leaves the payer on a spinner. Both are
// bounded so the payer always gets a way out.
const MOUNT_TIMEOUT = 15000;
const REDIRECTION_TIMEOUT = 600000;

// Lemonway's IIFE build assigns a global instead of exporting, so it needs a
// classic script tag: a dynamic import would keep its `var` in module scope and
// expose nothing. The promise is shared so reopening the funnel reuses the
// script already in the page, and cleared on failure so a retry can load again.
let sdkLoad = null;

const loadSdk = url => {
  if (window.LwHostedFields) return Promise.resolve(window.LwHostedFields);
  if (sdkLoad) return sdkLoad;

  sdkLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;

    script.addEventListener('load', () => {
      if (window.LwHostedFields) {
        resolve(window.LwHostedFields);
        return;
      }

      sdkLoad = null;
      reject(new Error('Lemonway SDK loaded without exposing LwHostedFields'));
    });

    script.addEventListener('error', () => {
      sdkLoad = null;
      script.remove();
      reject(new Error('Lemonway SDK could not be loaded'));
    });

    document.head.appendChild(script);
  });

  return sdkLoad;
};

export default class CardPaymentController extends Controller {
  static targets = [
    'loader',
    'fields',
    'submit',
    'error',
    'errorMessage',
    'frame',
  ];

  static values = {
    sdkUrl: String,
    iframeUrl: String,
    webkitToken: String,
    frameName: String,
    redirectUrl: String,
    statusUrl: String,
    successMessage: String,
    errorMessage: String,
    unavailableMessage: String,
    timeoutMessage: String,
    pendingMessage: String,
    schemeHeader: String,
    schemeCb: String,
    schemeVisa: String,
    schemeMastercard: String,
  };

  async connect() {
    this.settled = false;
    this.disconnected = false;
    this.redirecting = false;

    let hostedFields;
    try {
      const LwHostedFields = await loadSdk(this.sdkUrlValue);

      // Stimulus does not await connect(): the element may already be gone,
      // and mounting would attach a second SDK instance to the same ids.
      if (this.disconnected) return;

      hostedFields = new LwHostedFields(this.#config());
      hostedFields.mount();
      hostedFields.setSchemeLabels(this.#schemeLabels());
    } catch {
      if (!this.disconnected) this.#showError(this.unavailableMessageValue);
      return;
    }

    if (this.disconnected) {
      hostedFields.dispose();
      return;
    }

    this.hostedFields = hostedFields;
    this.mountTimeout = setTimeout(() => {
      if (!this.settled) this.#showError(this.unavailableMessageValue);
    }, MOUNT_TIMEOUT);
  }

  disconnect() {
    this.disconnected = true;
    clearTimeout(this.mountTimeout);
    clearTimeout(this.redirectionTimeout);

    if (this.hostedFields) {
      this.hostedFields.dispose();
      this.hostedFields = null;
    }
  }

  // Bound to the form's submit event, so this must swallow it first: letting
  // the native submission through would navigate away mid-payment.
  async submit(event) {
    if (event) event.preventDefault();
    if (!this.hostedFields || this.submitting) return;

    try {
      if (!(await this.hostedFields.validateForm())) return;
    } catch {
      this.#showError(this.errorMessageValue);
      return;
    }

    this.submitting = true;
    this.submitTarget.disabled = true;

    let result;
    try {
      result = await this.hostedFields.submit(false);
    } catch {
      this.#showError(this.errorMessageValue);
      return;
    }

    // webRedirection is optional. Its absence does not tell us the payment
    // failed, so the payer is sent back to their operations with a neutral
    // message and reconciliation settles the outcome.
    if (!result || !result.webRedirection) {
      this.#leaveForReconciliation();
      return;
    }

    this.#redirectInFrame(result.webRedirection);
  }

  // A frame with no src loads about:blank and fires this as soon as the step is
  // inserted, which would drop the spinner before the fields are mounted.
  frameLoaded() {
    if (!this.redirecting) return;

    this.loaderTarget.classList.add('d-none');
  }

  async handleMessage(event) {
    if (event.origin !== window.location.origin) return;
    if (!event.data || event.data.type !== 'lemonway-payment-callback') {
      return;
    }

    this.settled = true;
    clearTimeout(this.redirectionTimeout);

    let { status } = event.data;

    // Lemonway strips the parameters from the payer's return, so the frame
    // cannot tell the outcome and our own endpoint is asked instead.
    if (status === 'pending') {
      status = await this.#fetchStatus();
    }

    if (status === 'pending') {
      this.#leaveForReconciliation();
      return;
    }

    const message = status === 'success'
      ? this.successMessageValue
      : this.errorMessageValue;
    const type = status === 'success' ? 'success' : 'error';

    document.addEventListener('turbo:load', () => {
      showToast(type, message);
    }, { once: true });

    window.Turbo.visit(this.redirectUrlValue);
  }

  // The flat config shape is mandatory here: iframeUrl only exists on it, and
  // an SDK installed from npm cannot auto-discover where the fields are hosted.
  #config() {
    const config = {
      webkitToken: this.webkitTokenValue,
      holderName: this.#field('holderName'),
      cardNumber: this.#field('cardNumber'),
      expirationDate: this.#field('expirationDate'),
      cvv: this.#field('cvv'),
      scheme: {
        ...this.#field('scheme'),
        schemeIconSize: 'default',
      },
      // is-valid is not part of the design system, so a valid field simply
      // goes back to its neutral state.
      cssClass: {
        valid: '',
        notEmpty: '',
        invalid: 'is-invalid',
        focused: 'lw-hosted-field--focused',
      },
      onFormReady: () => this.#onFormReady(),
      onFormValidityChanged: (_source, isValid) => {
        if (!this.submitting) this.submitTarget.disabled = !isValid;
      },
    };

    if (this.iframeUrlValue) {
      config.iframeUrl = this.iframeUrlValue;
    }

    return config;
  }

  // Only font and colour declarations survive Lemonway's sanitizer, and only
  // under the `#text` id or the valid/invalid/not-empty classes; the box is
  // styled on our container instead. These rules stay inert while the iframe
  // serves `style-src 'self'`, which blocks the <style> the SDK injects.
  //
  // The container is resolved through data-field so the ids live in one place.
  #field(kind) {
    const container = this.element.querySelector(`[data-field="${kind}"]`);
    const containerId = container.id;
    const token = name => getComputedStyle(container)
      .getPropertyValue(`--lw-field-${name}`)
      .trim();

    const style = [
      '#text {',
      `  font-family: ${token('font')};`,
      // Absolute px: a rem would resolve against their root, and anything
      // under 16px makes Safari on iOS zoom the page on focus.
      `  font-size: ${getComputedStyle(container).fontSize};`,
      `  color: ${token('color')};`,
      '  outline: none;',
      '}',
      '#text::placeholder {',
      `  color: ${token('placeholder-color')};`,
      '}',
      '#text.invalid {',
      `  color: ${token('invalid-color')};`,
      '}',
      // Suppresses Chrome's autofill highlight, which is painted on the input
      // and therefore stops at our container's padding.
      '#text:-webkit-autofill,',
      '#text:-webkit-autofill:hover,',
      '#text:-webkit-autofill:focus {',
      '  transition: background-color 9999s ease-in-out 0s;',
      '}',
    ].join('\n');

    return {
      containerId,
      style,
      placeHolder: container.dataset.placeholder || '',
    };
  }

  async #fetchStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) return 'pending';

      const { status } = await response.json();

      return status || 'pending';
    } catch {
      return 'pending';
    }
  }

  #leaveForReconciliation() {
    showToast('info', this.pendingMessageValue);
    window.Turbo.visit(this.redirectUrlValue);
  }

  #schemeLabels() {
    return {
      header: this.schemeHeaderValue,
      options: {
        cb: this.schemeCbValue,
        visa: this.schemeVisaValue,
        mastercard: this.schemeMastercardValue,
      },
    };
  }

  #onFormReady() {
    this.settled = true;
    clearTimeout(this.mountTimeout);
    this.loaderTarget.classList.add('d-none');
    this.fieldsTarget.classList.remove('d-none');
  }

  #showError(message) {
    this.settled = true;
    clearTimeout(this.mountTimeout);
    clearTimeout(this.redirectionTimeout);
    this.loaderTarget.classList.add('d-none');
    this.fieldsTarget.classList.add('d-none');
    this.frameTarget.classList.add('d-none');
    this.errorMessageTarget.textContent = message;
    this.errorTarget.classList.remove('d-none');
  }

  // Lemonway hands back the redirection as data rather than performing it, so
  // we can target our own iframe instead of the top-level window.
  #redirectInFrame({ Verb, Uri, Fields }) {
    this.settled = false;
    this.redirecting = true;
    this.fieldsTarget.classList.add('d-none');
    this.loaderTarget.classList.remove('d-none');
    this.frameTarget.classList.remove('d-none');

    const form = document.createElement('form');
    form.method = Verb;
    form.action = Uri;
    form.target = this.frameNameValue;

    Object.entries(Fields || {}).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();

    this.redirectionTimeout = setTimeout(() => {
      if (!this.settled) this.#showError(this.timeoutMessageValue);
    }, REDIRECTION_TIMEOUT);
  }
}
